'use client';

import { cn } from '@common/utils';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useFloating,
} from '@floating-ui/react';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import { Slot } from '@radix-ui/themes';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { flushSync } from 'react-dom';

interface TooltipProps {
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (value: boolean) => void;
  onContentClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  defaultOpen?: boolean;
  floatingPadding?: number;
  delayDuration?: number;
  asChild?: boolean;
  label: React.ReactNode;
  className?: string;
  triggerClassName?: string;
  contentProps?: RadixTooltip.TooltipContentProps;
  isArrow?: boolean;
  /**
   * 툴팁이 표시될 방향을 지정합니다.
   * 'top': 트리거 요소 위에 툴팁 표시
   * 'bottom': 트리거 요소 아래에 툴팁 표시
   */
  side?: 'top' | 'bottom';
  /**
   * 툴팁 화살표를 기준으로 콘텐츠 박스의 정렬 방향을 지정합니다.
   * 'start': 왼쪽 정렬
   * 'center': 가운데 정렬 (기본값)
   * 'end': 오른쪽 정렬
   */
  align?: 'start' | 'center' | 'end';
}

export const Tooltip = ({
  children,
  defaultOpen = false,
  open,
  onOpenChange,
  onContentClick,
  label,
  floatingPadding = 10,
  asChild,
  className,
  delayDuration = 200,
  triggerClassName,
  contentProps,
  isArrow = false,
  side,
  align,
}: TooltipProps) => {
  const [localOpened, setLocalOpened] = useState(defaultOpen);

  const isOpened = open ?? localOpened;

  const [maxHeight, setMaxHeight] = useState<number>();

  const { context } = useFloating({
    onOpenChange: (value: boolean) => {
      setLocalOpened(value);
      onOpenChange?.(value);
    },
    open: isOpened,
    middleware: [
      offset(floatingPadding),
      flip(),
      shift({ padding: floatingPadding }),
      size({
        apply({ availableHeight }) {
          flushSync(() => {
            setMaxHeight(availableHeight - floatingPadding);
          });
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  context.floatingStyles.maxHeight = maxHeight;

  const Button = asChild ? Slot : 'button';

  const toggle = () => {
    setLocalOpened((prev) => !prev);
  };

  return (
    <RadixTooltip.Provider delayDuration={delayDuration}>
      <RadixTooltip.Root
        defaultOpen={defaultOpen}
        delayDuration={0}
        open={open ?? isOpened}
        onOpenChange={toggle}
      >
        <RadixTooltip.Trigger asChild onClick={toggle}>
          <Button className={triggerClassName}>{children}</Button>
        </RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content
            className={cn(
              'overflow-auto whitespace-pre-wrap break-all rounded-small bg-Gray-black/70 p-12',
              'font-400 text-Gray-white typo-caption1',
              'z-10 max-w-180',
              className,
            )}
            {...contentProps}
            sideOffset={floatingPadding}
            side={side}
            align={align}
            alignOffset={-10}
            onClick={onContentClick}
          >
            {label}
            {isArrow && (
              <RadixTooltip.Arrow
                className="opacity-70"
                width={14}
                height={8}
              />
            )}
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
};

interface TooltipProviderProps {
  children: ReactNode;
  delayDuration?: number;
}

export const TooltipProvider = ({
  children,
  delayDuration = 200,
}: TooltipProviderProps) => {
  return (
    <RadixTooltip.Provider delayDuration={delayDuration}>
      {children}
    </RadixTooltip.Provider>
  );
};

export const TooltipRoot = ({
  children,
  defaultOpen = false,
  onOpenChange,
  label,
  floatingPadding = 10,
  asChild,
  className,
  triggerClassName,
}: TooltipProps) => {
  const [isOpened, setIsOpened] = useState(defaultOpen);

  const [maxHeight, setMaxHeight] = useState<number>();

  const { context } = useFloating({
    onOpenChange: (value: boolean) => {
      setIsOpened(value);
      onOpenChange?.(value);
    },
    open: isOpened,
    middleware: [
      offset(floatingPadding),
      flip(),
      shift({ padding: floatingPadding }),
      size({
        apply({ availableHeight }) {
          flushSync(() => {
            setMaxHeight(availableHeight - floatingPadding);
          });
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  context.floatingStyles.maxHeight = maxHeight;

  const Button = asChild ? Slot : 'button';

  const toggle = () => {
    setIsOpened((prev) => !prev);
  };

  return (
    <RadixTooltip.Root
      defaultOpen={defaultOpen}
      delayDuration={0}
      open={isOpened}
      onOpenChange={toggle}
    >
      <RadixTooltip.Trigger asChild onClick={toggle}>
        <Button className={triggerClassName}>{children}</Button>
      </RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content
          className={cn(
            'overflow-auto whitespace-pre-wrap break-all rounded-small bg-Gray-black bg-opacity-70 p-12',
            'font-400 text-Gray-white typo-caption1',
            'z-10 max-w-180',
            className,
          )}
        >
          {label}
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  );
};
